/**
 * Fetches team member details from Firestore.
 * Supports legacy (memberDetails on team doc) and sharded (memberDetailsShardIds + memberDetailsShards subcollection).
 * Used by useTeamMemberDetails hook.
 *
 * @param {FirebaseFirestore.Firestore} db - Firestore instance (e.g. firebase.firestore())
 * @param {string} teamId - Team document ID in teamDatabase
 * @returns {Promise<Record<string, object>>} Map of userUID -> { userEmailid, userName, userType, ... }
 */

const SHARD_LIST_KEY = 'memberDetailsShardIds';
const SHARDS_COLLECTION = 'memberDetailsShards';
const SHARD_MEMBERS_FIELD = 'members';

export async function getTeamMemberDetails(db, teamId) {
  if (!db || !teamId) return {};
  const teamRef = db.collection('teamDatabase').doc(teamId);
  const teamSnap = await teamRef.get();
  if (!teamSnap.exists) return {};
  const data = teamSnap.data();

  // Legacy map on team doc (older teams)
  const legacyMap =
    data?.memberDetails && typeof data.memberDetails === 'object'
      ? data.memberDetails
      : {};

  // Sharded: memberDetailsShardIds + memberDetailsShards/*/members
  const shardIds = data?.[SHARD_LIST_KEY];
  let fromShards = {};
  if (Array.isArray(shardIds) && shardIds.length > 0) {
    const shardsRef = teamRef.collection(SHARDS_COLLECTION);
    const snapshots = await Promise.all(
      shardIds.map((id) => shardsRef.doc(id).get())
    );
    for (const snap of snapshots) {
      if (!snap.exists) continue;
      const shardData = snap.data();
      const members = shardData?.[SHARD_MEMBERS_FIELD];
      if (members && typeof members === 'object') {
        Object.assign(fromShards, members);
      }
    }
  }

  // Combined roster: legacy first, then shards (shard entry wins if same uid exists in both)
  return { ...legacyMap, ...fromShards };
}
